import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MagicContainer, MagicContainerContent, MagicContainerDescription, MagicContainerFooter, MagicContainerHeader, MagicContainerTitle } from "@/components/ui/MagicContainer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import WaveInput from "@/components/ui/WaveInput";
import Loader from "@/components/ui/Loader";
import { supabase } from "@/lib/supabase";

export default function LoginPage() {
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState("agent"); // 'agent' | 'agency_admin'
  const [npn, setNpn] = useState("");
  const [npnError, setNpnError] = useState("");
  const [searchParams] = useSearchParams();
  const [emailConfirmed, setEmailConfirmed] = useState(false); // Nuevo estado para error de NPN
  // Estado para debounce o checking
  const [checkingNpn, setCheckingNpn] = useState(false);

  // Nuevos estados para nombres
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [agencyName, setAgencyName] = useState("");

  // Estado del Auditor
  const [auditStatus, setAuditStatus] = useState({
    checked: false,
    ok: true,
    message: "Verificando sistema..."
  });

  // Ejecutar Auditoría al montar
  useEffect(() => {
    const runAudit = async () => {
      try {


        // 1. Verificación de Variables de Entorno
        const url = process.env.REACT_APP_SUPABASE_URL;
        const key = process.env.REACT_APP_SUPABASE_ANON_KEY;

        if (!url || !key) {
          throw new Error("Faltan variables de entorno (URL/KEY)");
        }

        // 2. Ping de Conectividad
        // Intentamos una lectura ligera que probablemente falle por RLS pero confirme conexión
        const { error } = await supabase.from('profiles').select('count', { count: 'exact', head: true });

        if (error) {
          // Fallo de red real
          if (error.message && error.message.includes("Failed to fetch")) {
            throw new Error("No hay conexión con Supabase (Network Error)");
          }
          // Errores de permisos son buenos aquí (significa que conectó)
          if (error.code === "PGRST301" || error.code === "42501") {

          } else {

          }
        }

        setAuditStatus({ checked: true, ok: true, message: "Sistema Operativo" });


      } catch (err) {

        setAuditStatus({
          checked: true,
          ok: false,
          message: `Error Crítico: ${err.message}`
        });
        toast.error("Error de Sistema detectado", { description: err.message });
      }
    };

    runAudit();
  }, []);

  // Detectar si el usuario viene de confirmar su email
  useEffect(() => {
    if (searchParams.get('confirmed') === 'true') {
      setEmailConfirmed(true);
      toast.success("¡Email confirmado exitosamente!", {
        description: "Ahora puedes iniciar sesión con tus credenciales.",
        duration: 6000
      });
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    try {
      setLoading(true);


      const data = await signIn(email, password);


      toast.success("Bienvenido de nuevo");

      // FIX: Determinar rol real del usuario desde la BD
      // El estado 'role' local solo sirve para el registro, no para el login
      const { data: profileData } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', data.user.id)
        .single();

      const userRole = profileData?.role || 'agent';

      let target = '/agent/dashboard';
      if (userRole === 'agency_admin' || userRole === 'dhm_supervisor') {
        target = '/agency/dashboard';
      } else if (userRole === 'fmo_admin') {
        target = '/fmo/dashboard';
      }

      navigate(target, {
        state: { showIntro: true },
        replace: true
      });

    } catch (error) {


      let msg = "Error al iniciar sesión";
      if (error.message.includes("Invalid login credentials")) msg = "Contraseña o correo incorrectos";
      if (error.message.includes("Email not confirmed")) msg = "Correo no confirmado";

      toast.error(msg, {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setNpnError(""); // Limpiar errores previos

    // Validación Dinámica
    if (!email || !password || !npn) {
      toast.error("Por favor completa todos los campos (Email, Password, NPN)");
      return;
    }

    if (role === 'agent' && (!firstName || !lastName)) {
      toast.error("Por favor ingresa tu Nombre y Apellido");
      return;
    }

    if (role === 'agency_admin' && !agencyName) {
      toast.error("Por favor ingresa el Nombre de la Agencia");
      return;
    }

    try {
      setLoading(true);

      // 1. Validar NPN Disponible (Pre-check)
      if (npn.length > 5) {
        setCheckingNpn(true);
        const { data: isAvailable, error: rpcError } = await supabase.rpc('check_npn_availability', {
          npn_to_check: npn
        });

        setCheckingNpn(false);

        if (rpcError) {

          // No bloqueamos por error de red en el check, dejamos que el registro intente
        } else if (isAvailable === false) {
          setNpnError("Este NPN ya está registrado.");
          toast.error("El NPN ingresado ya está asociado a una cuenta existente.");
          setLoading(false);
          return;
        }
      }



      const metadata = {
        role: role,
        npn: npn,
        // Datos condicionales
        first_name: role === 'agent' ? firstName : '',
        last_name: role === 'agent' ? lastName : '',
        agency_name: role === 'agency_admin' ? agencyName : '',
        full_name: role === 'agent' ? `${firstName} ${lastName}` : agencyName
      };

      const data = await signUp(email, password, metadata);



      if (data?.user?.identities?.length === 0) {
        toast.warning("Este correo ya está registrado. Intenta iniciar sesión.");
      } else {
        // Mostrar mensaje de confirmación de email
        toast.success("¡Cuenta creada exitosamente!", {
          description: "Revisa tu correo electrónico y haz clic en el enlace de confirmación para activar tu cuenta.",
          duration: 10000
        });
      }

    } catch (error) {
      toast.error("Error al registrarse", { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen px-4">
      <MagicContainer className="w-full max-w-md border-slate-800 bg-slate-900/60 backdrop-blur-xl text-slate-50">
        <MagicContainerHeader className="space-y-1 text-center">
          <MagicContainerTitle className="text-2xl font-bold tracking-tight text-white">
            Consoliday
          </MagicContainerTitle>
          <MagicContainerDescription className="text-slate-400">
            Plataforma de Gestión de Seguros
          </MagicContainerDescription>
        </MagicContainerHeader>
        <MagicContainerContent>
          {/* Alerta de Auditoría */}
          {!auditStatus.ok && auditStatus.checked && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-md flex items-center gap-2 text-sm text-red-400">
              <AlertCircle className="h-4 w-4" />
              <span>{auditStatus.message}</span>
            </div>
          )}

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-800">
              <TabsTrigger value="login">Ingresar</TabsTrigger>
              <TabsTrigger value="register">Registro</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4 pt-4">
              {emailConfirmed && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/50 rounded-md flex items-center gap-2 text-sm text-emerald-400">
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                  <span>¡Email confirmado! Ya puedes iniciar sesión.</span>
                </div>
              )}
              <form onSubmit={handleLogin} className="space-y-4">
                <WaveInput
                  label="Email Corporativo"
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="nombre@empresa.com"
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                <WaveInput
                  label="Contraseña"
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                  disabled={loading || !auditStatus.ok}
                >
                  {loading ? (
                    <>
                      <div className="mr-2"><Loader size={16} color="#ffffff" /></div>
                      Entrando...
                    </>
                  ) : (
                    "Iniciar Sesión"
                  )}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="space-y-4 pt-4">
              <form onSubmit={handleRegister} className="space-y-4">

                {/* Selección de Rol Primera */}
                <div className="space-y-2">
                  <Label>Tipo de Cuenta</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={role === 'agency_admin' ? 'default' : 'outline'}
                      className={`w-full h-9 text-xs px-1 ${role === 'agency_admin' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-transparent border-slate-700 hover:bg-slate-800 text-slate-300'}`}
                      onClick={() => setRole('agency_admin')}
                    >
                      Agencia
                    </Button>
                    <Button
                      type="button"
                      variant={role === 'agent' ? 'default' : 'outline'}
                      className={`w-full h-9 text-xs px-1 ${role === 'agent' ? 'bg-teal-600 hover:bg-teal-700' : 'bg-transparent border-slate-700 hover:bg-slate-800 text-slate-300'}`}
                      onClick={() => setRole('agent')}
                    >
                      Agente
                    </Button>
                  </div>
                </div>

                {/* Campos Dinámicos de Nombre */}
                {role === 'agency_admin' ? (
                  <div className="space-y-4 pt-2">
                    <WaveInput
                      label="Nombre de la Agencia"
                      id="agencyName"
                      type="text"
                      autoComplete="organization"
                      placeholder="Ej. Seguros Globales LLC"
                      className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                      value={agencyName}
                      onChange={(e) => setAgencyName(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <WaveInput
                      label="Nombre"
                      id="firstName"
                      type="text"
                      autoComplete="given-name"
                      placeholder="Juan"
                      className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={loading}
                    />
                    <WaveInput
                      label="Apellido"
                      id="lastName"
                      type="text"
                      autoComplete="family-name"
                      placeholder="Pérez"
                      className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                )}

                <WaveInput
                  label="Email Corporativo"
                  id="reg-email"
                  type="email"
                  autoComplete="email"
                  placeholder="nombre@empresa.com"
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />

                <WaveInput
                  label="NPN (Licencia)"
                  id="npn"
                  type="text"
                  placeholder="1234567"
                  className={`bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 ${npnError ? "border-red-500" : ""}`}
                  value={npn}
                  autoComplete="off" // NPN no es estándar para autocompletar
                  onChange={(e) => {
                    setNpn(e.target.value.replace(/\D/g, ''));
                    setNpnError(""); // Limpiar error al escribir
                  }}
                  disabled={loading}
                  maxLength={10}
                />
                {npnError && <p className="text-xs text-red-400 font-medium">{npnError}</p>}

                <WaveInput
                  label="Contraseña"
                  id="reg-password"
                  type="password"
                  placeholder="••••••••"
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  className={`w-full text-white ${role === 'agency_admin' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-teal-600 hover:bg-teal-700'}`}
                  disabled={loading || !auditStatus.ok}
                >
                  {loading ? (
                    <>
                      <div className="mr-2"><Loader size={16} color="#ffffff" /></div>
                      Registrando...
                    </>
                  ) : (
                    role === 'agency_admin' ? "Solicitar Cuenta de Agencia" : "Registrarme como Agente"
                  )}
                </Button>
                <p className="text-xs text-center text-slate-400">
                  {role === 'agency_admin'
                    ? "Requiere aprobación manual."
                    : "Acceso inmediato."}
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </MagicContainerContent>
        <MagicContainerFooter className="flex flex-col space-y-2 text-center text-sm text-slate-500">
          <p>DHM Taxes & More LLC © 2026</p>

          {/* Indicador de Estado */}
          <div className={`flex items-center justify-center gap-2 text-xs font-mono px-2 py-1 rounded-full w-fit mx-auto mt-2 ${auditStatus.ok ? "text-emerald-500 bg-emerald-500/10" : "text-red-500 bg-red-500/10"
            }`}>
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${auditStatus.ok ? "bg-emerald-400" : "bg-red-400"
                }`}></span>
              <span className={`relative inline-flex rounded-full h-2 w-2 ${auditStatus.ok ? "bg-emerald-500" : "bg-red-500"
                }`}></span>
            </span>
            {auditStatus.message}
          </div>
        </MagicContainerFooter>
      </MagicContainer>
    </div>
  );
}
